<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useI18n } from "vue-i18n";

import { toast } from "@/common/utils/toast";

import { consoleGet } from "@/common/api/rest/console/console";

import type { ConsoleType } from "@/common/types/console/console";

import TabConsoleInfos from "@/components/tabs/console/infos.vue";
import TabConsoleDetails from "@/components/tabs/console/details.vue";
import TabConsoleGames from "@/components/tabs/console/games.vue";
import TabConsoleImages from "@/components/tabs/console/images.vue";
import TabConsoleSlug from "@/components/tabs/console/slug.vue";
import TabConsoleName from "@/components/tabs/console/name.vue";
import TabConsoleAccessory from "@/components/tabs/console/accessory.vue";

import { breadcrumbSimple, iconMenu, svgIcon } from "@/common/components";

const { t } = useI18n();
const router = useRouter();
const route = useRoute();

const selectedTab = ref("infos");
const consoleData = ref<ConsoleType | null>(null);

const consoleId = computed(() => route.params.item as string);

const breadcrumbList = computed(() => [
  {
    key: "collections",
    label: t("common.labels.collections"),
    url: `/collection`,
  },
  {
    key: "collection",
    label: t("collection.labels.consoles"),
    url: `/collection/console`,
  },
  {
    key: "edit",
    label: consoleData.value?.name ?? "...",
  },
]);

const menuList = computed(() => [
  {
    key: "infos",
    icon: "field",
    label: t("collection.labels.infos"),
    url: `/collection/console/${consoleId.value}/infos`,
  },
  {
    key: "name",
    icon: "no-image",
    label: t("common.labels.other_names"),
    url: `/collection/console/${consoleId.value}/name`,
  },
  {
    key: "slug",
    icon: "no-image",
    label: t("slug.labels.slugs"),
    url: `/collection/console/${consoleId.value}/slug`,
  },
  {
    key: "image",
    icon: "no-image",
    label: t("collection.labels.images"),
    url: `/collection/console/${consoleId.value}/image`,
  },
  {
    key: "details",
    icon: "no-image",
    label: t("collection.labels.details"),
    url: `/collection/console/${consoleId.value}/details`,
  },
  {
    key: "game",
    icon: "game",
    label: t("collection.labels.games"),
    url: `/collection/console/${consoleId.value}/game`,
  },
  {
    key: "accessory",
    icon: "gamepad",
    label: t("collection.labels.accessories"),
    url: `/collection/console/${consoleId.value}/accessory`,
  },
]);

function back() {
  router.push(`/collection/console`);
}

async function changeMenu(url: string) {
  await fetchData();
  router.push(url);
}

// console
async function fetchData() {
  await consoleGet(consoleId.value)
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        consoleData.value = data;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

watch(
  () => route.params.tab,
  () => {
    selectedTab.value = route.params.tab as string;
  },
  {
    immediate: true,
  },
);

onMounted(async () => {
  await fetchData();
});
</script>

<template>
  <article class="register-page">
    <section class="page-header">
      <breadcrumb-simple
        :items="breadcrumbList"
        :show-home="true"
        url-home="/home"
        active="edit"
      />
    </section>

    <section class="page-content">
      <div class="tab-header">
        <div class="title">
          <svg-icon icon="gamepad" size="2rem" />
          EDITAR CONSOLE
        </div>

        <div class="actions">
          <button class="btn btn-default w-8 h-8" @click="back()">
            <svg-icon icon="close" size="1rem" />
          </button>
        </div>
      </div>

      <div class="tab-content">
        <icon-menu
          :tabs="menuList"
          :active="selectedTab"
          size="3rem"
          @selected="changeMenu($event)"
        />

        <template v-if="consoleData">
          <template v-if="selectedTab === 'infos'">
            <tab-console-infos
              :console-id="consoleId"
              :console="consoleData"
              @update:data="fetchData()"
            />
          </template>

          <template v-if="selectedTab === 'image'">
            <tab-console-images :console-id="consoleId" />
          </template>

          <template v-if="selectedTab === 'game'">
            <tab-console-games :console-id="consoleId" />
          </template>

          <template v-if="selectedTab === 'accessory'">
            <tab-console-accessory :console-id="consoleId" />
          </template>

          <template v-if="selectedTab === 'slug'">
            <tab-console-slug :console-id="consoleId" />
          </template>

          <template v-if="selectedTab === 'name'">
            <tab-console-name :console-id="consoleId" />
          </template>

          <template v-if="selectedTab === 'details'">
            <tab-console-details :console-id="consoleId" />
          </template>
        </template>
      </div>
    </section>
  </article>
</template>

<style lang="scss" scoped>
.register-page {
  display: flex;
  flex-direction: column;
  gap: 1rem;

  .page-header {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    width: inherit;
    background: white;
    border-radius: 0.5rem;
    padding-inline: 1rem;
    padding-block: 0.5rem;
  }

  .page-content {
    display: flex;
    flex-direction: column;
    background: white;
    border-radius: 0.5rem;

    .tab-header {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: space-between;
      gap: 0.5rem;
      padding-inline: 1.25rem;
      padding-block: 0.75rem;
      margin-block-end: 1.5rem;
      border-block-end: 1px solid var(--border);

      .title {
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 0.5rem;
        font-size: 1.2rem;
        text-transform: uppercase;
      }

      .actions {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: flex-end;
        gap: 0.75rem;

        button {
          width: 1.75rem;
          height: 1.75rem;
          padding: 0;
        }
      }
    }

    .tab-content {
      display: flex;
      flex-direction: column;
      gap: 1.54rem;
      padding-inline: 1.25rem;
      padding-block-end: 1.25rem;
    }
  }
}
</style>
